import {
  Avatar,
  Box,
  Button,
  Divider,
  Link,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useCallback } from 'react'
import {
  IconButton,
  Icons,
  InformationModal,
  ScrollTag,
  SerialNumber,
  SkeletonText,
} from 'utoniq-ui'

import {
  Avatar as SchemaAvatar,
  ItemThumbnailType,
  Maybe,
  Scalars,
  SerialItemOwnersFragment,
  TicketItemExpirationTerm,
  TicketItemMetadataAssignedNumberType,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ItemReversibleThumbnail, ItemStatusCell } from '@/components/organisms'
import {
  FullscreenModal,
  TableScrollFetcher,
} from '@/components/organisms/functionals'
import {
  ConsoleSupplementLinkArea,
  ConsoleSupplementLinkAreaProps,
} from '@/components/pages/ConsoleItemDetail/template/ConsoleSupplementLinkArea'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { styleSets } from '@/components/styles'
import { constants } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { dayjs } from '@/lib/dayjs'

import { State } from './'
import { ConsoleItemDescription } from './ConsoleItemDescription'

export type ConsoleItemDetailTicketProps = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  artistId: string
  ticketOpen?: string
  item: State['item']
  onToggleActive: ConsoleSupplementLinkAreaProps['onToggleActive']
  onToggleSelling: ConsoleSupplementLinkAreaProps['onToggleSelling']
  onRemove: ConsoleSupplementLinkAreaProps['onRemove']
  onFetchMore: () => Promise<void>
  isFlipped: boolean
  onReverseThumbnail: () => void
  onOpenFullScreen: () => void
  onOpenMultipleImage: () => void
  ticketFilter: TicketFilterType
  onFilterTicket: (filter: TicketFilterType) => Promise<void>
  onToggleTicketUsed: (
    serialId: string,
    isUsed: boolean,
    throughTicketName?: string
  ) => Promise<void>
}
//チケット情報表示用の型
type TicketInfo = {
  id: Scalars['ID']
  ticketStatus?: Maybe<{
    isUsed: Scalars['Boolean']
    formattedUsedAt?: Maybe<Scalars['String']>
    throughTicket: {
      index: Scalars['Int']
      isUsed: Scalars['Boolean']
      seat: Scalars['String']
      name: Scalars['String']
      formattedUsedAt?: Maybe<Scalars['String']>
    }
  }>
  formattedSerialNumber: Scalars['String']
  isNft?: Maybe<Scalars['Boolean']>
  user?: {
    displayName: Scalars['String']
    fullName: Scalars['String']
    id: Scalars['ID']
    phoneNumber: Scalars['String']
    avatar?: Maybe<SchemaAvatar>
  }
  formattedAssignedNumber?: Maybe<Scalars['String']>
}

export const ConsoleItemDetailTicket: Cell<ConsoleItemDetailTicketProps> = ({
  loading,
  artistId,
  item,
  ticketOpen,
  onRemove,
  onToggleSelling,
  onToggleActive,
  refetching,
  hasNextPage,
  onFetchMore,
  isFlipped,
  onReverseThumbnail,
  onOpenFullScreen,
  onOpenMultipleImage,
  ticketFilter,
  onFilterTicket,
  onToggleTicketUsed,
}) => {
  const [open, setOpen] = useState(ticketOpen === 'true')
  const { router } = useRouter()
  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ThumbnailActionAreaCell
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
          numberImage={item?.images?.length ?? 0}
        />
        <ItemReversibleThumbnail
          loading={loading}
          flipProps={{ isFlipped }}
          imageAreaProps={{
            thumbnail: item?.thumbnail,
            size:
              item?.thumbnailType === ItemThumbnailType.Square
                ? 'x-large-square'
                : 'x-large',
            onClick: onReverseThumbnail,
          }}
        />
      </Box>
      {item && !loading && (
        <Stack sx={{ pt: 3 }}>
          <ItemStatusCell
            salesCondition={item.salesCondition}
            isPublished={item.isPublished}
            isDrm={item.isDrm}
            isPremium={item.isPremium}
            isThroughTicket={!!item.metadata?.ticket?.isThroughTicket}
          />
        </Stack>
      )}
      <ConsoleItemDescription item={item} loading={loading} />

      <Box sx={{ mt: 6, mb: 2 }}>
        {loading && <Skeleton width={30} height={30} variant="circular" />}
        {!loading && (
          <IconButton.Ticket
            size="small"
            onClick={() => setOpen(true)}
            bgcolor="primary.main"
            color="white"
          />
        )}
      </Box>
      <TicketCellModal
        artistId={artistId}
        open={open}
        onClose={() => {
          if (ticketOpen === 'true') {
            return router.goBack()
          }
          setOpen(false)
        }}
        item={item}
        loading={loading || refetching}
        hasNextPage={hasNextPage}
        onToggleActive={onToggleActive}
        onToggleSelling={onToggleSelling}
        onRemove={onRemove}
        onFetchMore={onFetchMore}
        ticketFilter={ticketFilter}
        onFilterTicket={onFilterTicket}
        onToggleTicketUsed={onToggleTicketUsed}
      />
    </>
  )
}

type TicketCellModalProps = {
  open: boolean
  onClose: () => void
  artistId: string
  loading: boolean
  hasNextPage: boolean
  item: ConsoleItemDetailTicketProps['item']
  onToggleActive: ConsoleItemDetailTicketProps['onToggleActive']
  onToggleSelling: ConsoleItemDetailTicketProps['onToggleSelling']
  onRemove: ConsoleItemDetailTicketProps['onRemove']
  onFetchMore: ConsoleItemDetailTicketProps['onFetchMore']
  ticketFilter: TicketFilterType
  onFilterTicket: (filter: TicketFilterType) => Promise<void>
  onToggleTicketUsed: (
    serialId: string,
    isUsed: boolean,
    throughTicketName?: string
  ) => Promise<void>
}
const TicketCellModal: Cell<TicketCellModalProps> = ({
  open,
  onClose,
  artistId,
  loading,
  hasNextPage,
  item,
  onRemove,
  onToggleSelling,
  onToggleActive,
  onFetchMore,
  ticketFilter,
  onFilterTicket,
  onToggleTicketUsed,
}) => {
  const { t } = useTranslation('consoleItemDetail')
  const [editable, setEditable] = useState<boolean>(false)
  const ticketExpired = useMemo<string>(() => {
    switch (item?.metadata?.ticket?.expirationTerm) {
      case TicketItemExpirationTerm.Halfyear:
        return t('6months')
      case TicketItemExpirationTerm.Oneyear:
        return t('1year')
      default:
        return t('unlimited')
    }
  }, [item?.metadata?.ticket?.expirationTerm, t])

  const [ticketModalSerial, setTicketModalSerial] = useState<TicketInfo | null>(
    null
  )
  const [subTicketName, setSubTicketName] = useState<string | undefined>()

  const [subTicketIsUsed, setSubTicketIsUsed] = useState<boolean>(false)

  const [ticketButtonModalLoading, setTicketButtonModalLoading] =
    useState(false)

  //Typeが座席かどうか判定
  const assignedNumberType =
    item?.metadata?.ticket?.assignedNumberType ===
    TicketItemMetadataAssignedNumberType.Seat
      ? true
      : false
  //通し券かどうか判定
  const isThroughTicket = !!item?.metadata?.ticket?.isThroughTicket

  const purchasedNumber = useMemo(
    () =>
      item?.purchasedNumber || item?.pendingPurchaseNumber
        ? (item?.purchasedNumber ?? 0) + (item?.pendingPurchaseNumber ?? 0)
        : undefined,
    [item?.pendingPurchaseNumber, item?.purchasedNumber]
  )

  useEffect(() => {
    //サブチケット名
    const name = ticketModalSerial?.ticketStatus?.throughTicket
      ? ticketModalSerial.ticketStatus.throughTicket.name ?? undefined
      : undefined
    //サブチケットが使われたかどうか
    const isUsed = ticketModalSerial?.ticketStatus?.throughTicket
      ? !!ticketModalSerial.ticketStatus.throughTicket.isUsed
      : !!ticketModalSerial?.ticketStatus?.isUsed

    setSubTicketName(name)
    setSubTicketIsUsed(isUsed)
  }, [ticketModalSerial])

  return (
    <FullscreenModal
      open={open}
      onClose={() => onClose()}
      sx={{ zIndex: ({ zIndex }) => zIndex?.modal - 1 }}
    >
      <ConsoleBaseLayout
        customNavigation={
          <ConsoleSupplementLinkArea
            loading={false}
            itemId={item?.id ?? ''}
            isPublished={!!item?.isPublished}
            saleStatus={item?.salesStatus ?? ''}
            onToggleActive={onToggleActive}
            onToggleSelling={onToggleSelling}
            onRemove={onRemove}
          />
        }
        header={{
          left: (
            <IconButton.Close
              edge="start"
              color="white"
              size="medium"
              onClick={onClose}
            />
          ),
          title: t('ticketInfoTitle'),
        }}
        customNavigationSx={{
          bgcolor: 'background.default',
          maxWidth: constants.ui.layout.mainContentMaxWidth,
        }}
        sx={{
          header: {
            bgcolor: 'primary.main',
            '.MuiTypography-root, svg': { color: 'white' },
          },
        }}
      >
        <Stack flex={1}>
          <Stack spacing={6} sx={{ px: 5, pt: 6 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icons.Post fontSize="small" sx={{ color: 'grey.600' }} />
                <Typography variant="h6" color="text.secondary">
                  {t('ticketContent')}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {item?.metadata?.ticket?.description}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icons.Timer fontSize="small" sx={{ color: 'grey.600' }} />
                <Typography variant="h6" color="text.secondary">
                  {t('ticketExpiredAt')}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {item?.metadata?.ticket?.expirationTerm === null
                  ? t('unlimited')
                  : ticketExpired}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icons.Info fontSize="small" sx={{ color: 'grey.600' }} />
                <Typography variant="h6" color="text.secondary">
                  {t('ticketNote')}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {item?.metadata?.ticket?.note}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" color="text.secondary">
                  {t('startAssignedNumber')}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {item?.metadata?.ticket?.assignedNumberPrefix}-
                {item?.metadata?.ticket?.startAssignedNumber}
              </Typography>
            </Stack>
            <Stack spacing={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icons.Serial fontSize="small" sx={{ color: 'grey.600' }} />
                <Typography variant="h6" color="text.secondary">
                  {t('ticketStatus', 'チケット状況')}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Stack
                  justifyContent="space-between"
                  direction="row"
                  alignItems="center"
                  sx={{
                    borderWidth: 1,
                    borderColor: 'grey.100',
                    borderStyle: 'solid',
                    borderRadius: 1,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ maxWidth: 120, minWidth: 110 }}
                  >
                    {t('purchasePerStock', '購入数/在庫数')}
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                  >
                    <Typography variant="subtitle1">
                      {purchasedNumber ?? '-'}/
                    </Typography>
                    <Typography variant="subtitle1">
                      {item?.issuedNumber ?? '-'}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  justifyContent="space-between"
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    borderWidth: 1,
                    borderColor: 'grey.100',
                    borderStyle: 'solid',
                    borderRadius: 1,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ maxWidth: 120, minWidth: 110 }}
                  >
                    {t('usedUnusedPerPurchase', '使用済み(未使用)/\n購入数')}
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                  >
                    <Typography variant="subtitle1" component="span">
                      {item?.usedNumber ?? '-'}
                      <Typography
                        variant="subtitle1"
                        component="span"
                        color="warning.main"
                      >
                        (
                        {purchasedNumber
                          ? purchasedNumber - (item?.usedNumber ?? 0)
                          : '-'}
                        )
                      </Typography>
                      /
                    </Typography>
                    <Typography variant="subtitle1">
                      {purchasedNumber ?? '-'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <FilterMenu
            filter={ticketFilter}
            onFilter={onFilterTicket}
            isThroughTicket={isThroughTicket}
          />
          {!item?.collection?.isEventPickup && (
            <Stack
              justifyContent="flex-end"
              alignItems="flex-end"
              sx={{ px: 4 }}
            >
              <Button
                variant="text"
                fullWidth={false}
                onClick={() => setEditable(prevState => !prevState)}
              >
                {editable
                  ? t('completion', '完了')
                  : t('editUsage', '使用状況を編集する')}
              </Button>
            </Stack>
          )}
          <SerialTables
            editable={editable}
            artistId={artistId}
            loading={loading}
            item={item}
            serials={item?.serials}
            onFetchMore={onFetchMore}
            hasNextPage={hasNextPage}
            setTicketModalSerial={setTicketModalSerial}
            assignedNumberType={assignedNumberType}
            ticketFilter={ticketFilter}
            isThroughTicket={isThroughTicket}
          />
        </Stack>
      </ConsoleBaseLayout>

      <InformationModal
        open={!!ticketModalSerial}
        onClose={() => setTicketModalSerial(null)}
        title={t('TicketUseModalTitle', '使用状況を変更しますか？')}
        content={
          subTicketIsUsed
            ? t(
                'TicketUseModalUseDesc',
                '使用状況を使用済みから未使用に変更します。'
              )
            : t(
                'TicketUseModalUnuseDesc',
                '使用状況を未使用から使用済みに変更します。'
              )
        }
        cancelText={t('close', '閉じる')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            loading={ticketButtonModalLoading}
            disabled={ticketButtonModalLoading}
            onClick={() => {
              if (!ticketModalSerial) return

              setTicketButtonModalLoading(true)

              onToggleTicketUsed(
                ticketModalSerial.id,
                subTicketIsUsed,
                subTicketName
              ).finally(() => {
                setTicketModalSerial(null)
                setTicketButtonModalLoading(false)
              })
            }}
          >
            {subTicketIsUsed
              ? t('TicketUseModalUseButton', '未使用に変更する')
              : t('TicketUseModalUnuseButton', '使用済みに変更する')}
          </Button>
        }
      />
    </FullscreenModal>
  )
}

type SerialTablesProps = {
  editable: boolean
  artistId: string
  loading: boolean
  hasNextPage: boolean
  item: ConsoleItemDetailTicketProps['item']
  serials?: SerialItemOwnersFragment
  assignedNumberType: boolean
  ticketFilter: TicketFilterType
  isThroughTicket: boolean
  onFetchMore: () => Promise<void>
  setTicketModalSerial: (serial: TicketInfo | null) => void
}
export const SerialTables: Cell<SerialTablesProps> = ({
  editable,
  artistId,
  serials,
  loading,
  hasNextPage,
  item,
  assignedNumberType,
  ticketFilter,
  isThroughTicket,
  onFetchMore,
  setTicketModalSerial,
}) => {
  const { t } = useTranslation('consoleItemDetail')
  const { generatePath, routerPages } = useRouter()
  const [filter, setFitler] = useState<string>()

  useEffect(() => {
    setFitler(ticketFilter)
  }, [ticketFilter])

  //通し券があった場合に、node.ticketStatus.throughTicketの中身を一つずつ取り出して、throughTicket.nameごとの配列をつくる処理
  const mappedTickets =
    serials?.edges.map(({ node }) => {
      const throughTickets = node.ticketStatus?.throughTicket
      //サブチケットがあるか判定
      if (!throughTickets) {
        return [node]
      } else {
        const namesArray = throughTickets?.map(ticket => ticket.name) || []
        return (
          namesArray.map(name => {
            const matchedTicket = node.ticketStatus?.throughTicket?.find(
              ticket => ticket.name === name
            )
            return {
              ...node,
              ticketStatus: {
                ...node.ticketStatus,
                throughTicket: {
                  ...matchedTicket,
                },
              },
            }
          }) || []
        )
      }
    }) || []
  const updatedSerialsArray = mappedTickets.flat() as TicketInfo[]

  //通し券があった場合のフィルター処理
  const filteredTickets = updatedSerialsArray
    .filter(({ ticketStatus }: TicketInfo) => {
      // サブチケットがあるか判定
      if (!ticketStatus?.throughTicket) {
        return true
      } else {
        switch (filter) {
          case 'used':
            return ticketStatus?.throughTicket?.isUsed === true
          case 'unused':
            return ticketStatus?.throughTicket?.isUsed === false
          default:
            return true
        }
      }
    })
    //配列の要素の前後のシリアルナンバーを比較し、異なっていれば配列の先頭であることを表すisFirstをtrueにする
    .map((ticket, index, array) => ({
      ...ticket,
      isFirst:
        index === 0 ||
        ticket.formattedSerialNumber !==
          array[index - 1]?.formattedSerialNumber, // 先頭判定
    }))

  const border = {
    borderTop: '1px solid',
    borderColor: 'grey.100',
  }

  // サブチケット名カラムのスタイルを取得する関数
  const subTicketColumnStyle = (index: number, isFirst: boolean) => {
    if (index === 0) return {}
    if (isFirst) {
      return border
    } else {
      return {}
    }
  }
  return (
    <CellFrame sx={{ pb: 4, px: 2 }}>
      <TableContainer>
        <Table
          aria-label="serial-number-table"
          sx={{
            width: 'auto',
            ovrerflowX: 'scroll',
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ border: 'none', minWidth: 52, py: 2 }}>
                <Typography
                  variant="overline"
                  color="text.hint"
                  sx={{ display: 'block' }}
                >
                  {t('isUsed', '使用状況')}
                </Typography>
              </TableCell>
              <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 2 }}>
                <Typography
                  variant="overline"
                  color="text.hint"
                  sx={{ display: 'block' }}
                >
                  {t('serialNumber', 'シリアルナンバー')}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ border: 'none', minWidth: 88, py: 2, px: 3 }}
              >
                <Typography variant="overline" color="text.hint">
                  {assignedNumberType
                    ? t('seat', '座席番号')
                    : t('assignedNumber', '整理番号')}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  border: 'none',
                  minWidth: 98,
                  py: 2,
                  px: 3,
                }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('subTicketName', 'サブチケット名')}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ border: 'none', minWidth: 128, py: 2, px: 3 }}
              >
                <Typography
                  variant="overline"
                  color="text.hint"
                  sx={{ display: 'block' }}
                >
                  {t('dateOfUse')}
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ border: 'none', minWidth: 88, py: 2, px: 3 }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('usedAt', '使用日')}
                </Typography>
              </TableCell>
              <TableCell
                width="100%"
                align="left"
                sx={{ border: 'none', minWidth: 150, py: 2, px: 3 }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('fullName', '氏名')}
                </Typography>
              </TableCell>
              <TableCell
                width="100%"
                align="left"
                sx={{ border: 'none', minWidth: 150, py: 2, px: 3 }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('owner', '所有者')}
                </Typography>
              </TableCell>
              <TableCell
                width="100%"
                align="left"
                sx={{ border: 'none', py: 2, px: 3, minWidth: '100px' }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('phoneNumber', '電話番号')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            component={TableRow}
            colSpan={5}
            nodes={filteredTickets.map((serial, index) => (
              <Fragment key={serial.id}>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    px: 0,
                    minWidth: 52,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: 64 }}
                  >
                    {editable && (
                      <>
                        {serial?.ticketStatus?.throughTicket ? (
                          serial.ticketStatus.throughTicket.isUsed ? (
                            <IconButton.Selected
                              size="small"
                              color="primary.main"
                              sx={{ mx: 'auto' }}
                              onClick={() => setTicketModalSerial(serial)}
                            />
                          ) : (
                            <IconButton.Circle
                              size="small"
                              sx={{ mx: 'auto' }}
                              onClick={() => setTicketModalSerial(serial)}
                            />
                          )
                        ) : serial.ticketStatus?.isUsed ? (
                          <IconButton.Selected
                            size="small"
                            color="primary.main"
                            sx={{ mx: 'auto' }}
                            onClick={() => setTicketModalSerial(serial)}
                          />
                        ) : (
                          <IconButton.Circle
                            size="small"
                            sx={{ mx: 'auto' }}
                            onClick={() => setTicketModalSerial(serial)}
                          />
                        )}
                      </>
                    )}
                    {!editable && (
                      <>
                        {serial?.ticketStatus?.throughTicket ? (
                          serial.ticketStatus.throughTicket.isUsed ? (
                            <Icons.Check
                              color="primary"
                              sx={{ mx: 'auto' }}
                              fontSize="small"
                            />
                          ) : (
                            <Typography
                              variant="overline"
                              align="center"
                              sx={{ px: 2, width: '100%' }}
                            >
                              {'-'}
                            </Typography>
                          )
                        ) : serial.ticketStatus?.isUsed ? (
                          <Icons.Check
                            color="primary"
                            sx={{ mx: 'auto' }}
                            fontSize="small"
                          />
                        ) : (
                          <Typography
                            variant="overline"
                            align="center"
                            sx={{ px: 2, width: '100%' }}
                          >
                            {'-'}
                          </Typography>
                        )}
                      </>
                    )}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 20,
                        border: '1px solid',
                        borderColor: 'grey.100',
                      }}
                    />
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    pl: 3,
                    pr: 0,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ pr: 1 }}
                  >
                    <SerialNumber label={serial.formattedSerialNumber} />
                    {serial.isNft && <Icons.Nft fontSize="small" filledColor />}
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    px: 0,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 20,
                        border: '1px solid',
                        borderColor: 'grey.100',
                      }}
                    />
                    <Typography variant="overline">
                      {isThroughTicket
                        ? assignedNumberType
                          ? serial.ticketStatus?.throughTicket.seat ?? '-'
                          : serial.formattedAssignedNumber ?? '-'
                        : serial.formattedAssignedNumber ?? '-'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    pl: 0,
                    pr: 5,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Stack>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: 20,
                          border: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      />
                    </Stack>
                    <Typography variant="overline" sx={styleSets.lineCamp(2)}>
                      {serial.ticketStatus?.throughTicket?.name ?? '-'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    px: 0,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ height: 20 }}
                    />
                    <Typography variant="overline">
                      {(() => {
                        const formatDate = (date?: string) =>
                          date ? dayjs(date).format('YYYY/MM/DD HH:mm') : '-'
                        const ticket = isThroughTicket
                          ? item?.metadata?.ticket?.throughTicket?.[
                              serial.ticketStatus?.throughTicket?.index ?? 0
                            ]
                          : item?.metadata?.ticket
                        return [ticket?.startDateOfUse, ticket?.endDateOfUse]
                          .map(formatDate)
                          .join('\n')
                      })()}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    py: 2,
                    px: 0,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 20,
                        border: '1px solid',
                        borderColor: 'grey.100',
                      }}
                    />
                    <Typography variant="overline">
                      {serial?.ticketStatus?.throughTicket
                        ? serial.ticketStatus.throughTicket.formattedUsedAt ??
                          '-'
                        : serial.ticketStatus?.formattedUsedAt ?? '-'}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    pl: 0,
                    pr: 5,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Stack>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: 20,
                          border: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      />
                    </Stack>
                    {serial.user && (
                      <Typography variant="overline">
                        {serial.user.fullName}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    pl: 0,
                    pr: 5,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Stack>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          height: 20,
                          border: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      />
                    </Stack>
                    {serial.user && (
                      <Stack
                        component={Link}
                        underline="none"
                        href={generatePath(routerPages.ConsoleUserDetail, {
                          userId: serial.user.id,
                        })}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                      >
                        <Avatar
                          src={serial.user.avatar?.smallUri ?? ''}
                          sx={{ width: 28, height: 28 }}
                        />
                        <Typography variant="overline">
                          {serial.user.displayName}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    position: 'relative',
                    py: 2,
                    px: 0,
                    border: 'none',
                    bgcolor: serial.user?.id === artistId ? 'grey.50' : 'unset',
                    ...subTicketColumnStyle(index, serial.isFirst),
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 20,
                        border: '1px solid',
                        borderColor: 'grey.100',
                      }}
                    />
                    <Typography variant="overline">
                      {serial.user?.phoneNumber}
                    </Typography>
                  </Stack>
                </TableCell>
              </Fragment>
            ))}
            loadingComponent={
              <TableRow>
                <TableCell padding="none" colSpan={3} sx={{ border: 'none' }}>
                  <Stack flex={1} spacing={3} sx={{ px: 5 }}>
                    <SkeletonText
                      variant="large"
                      numberOfLine={8}
                      lastWidth="100%"
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            }
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

type TicketFilterType = 'general' | 'used' | 'unused'

const FilterMenu = ({
  filter,
  onFilter,
  isThroughTicket,
}: {
  filter: TicketFilterType
  onFilter: (filter: TicketFilterType) => void
  isThroughTicket: boolean
}) => {
  const { t } = useTranslation('consoleItemDetail')

  const handleSelect = useCallback(
    (value: string) => {
      onFilter(value as TicketFilterType)
    },
    [onFilter]
  )

  return (
    <Box sx={{ px: 5, pt: 6, pb: 1 }}>
      <Stack direction="row" spacing={1} pb={1} alignItems="center">
        <Icons.Serial fontSize="small" sx={{ color: 'grey.600' }} />
        <Typography variant="h6" color="text.secondary">
          {t('usageHistory', '使用履歴')}
        </Typography>
      </Stack>
      <ScrollTag
        value={filter}
        onClick={handleSelect}
        tags={[
          { value: 'general', label: t('general') },
          { value: 'used', label: t('used') },
          { value: 'unused', label: t('unused') },
        ]}
      />
    </Box>
  )
}
